import React, { useState } from 'react';
import { Bell, Send, History, FileText, Settings as SettingsIcon, Plus, Edit, Trash2, Copy, X, Save } from 'lucide-react';

const Notifications = () => {
  const [activeTab, setActiveTab] = useState('send');
  const [showSendModal, setShowSendModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  
  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationMessage, setNotificationMessage] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('general');
  const [selectedTargetRole, setSelectedTargetRole] = useState('all');
  
  const [templateTitle, setTemplateTitle] = useState('');
  const [templateMessage, setTemplateMessage] = useState('');
  const [templateCategory, setTemplateCategory] = useState('general');
  const [templateTargetRole, setTemplateTargetRole] = useState('all');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [notificationHistory, setNotificationHistory] = useState([
    {
      id: '1',
      title: 'System Maintenance',
      message: 'Scheduled maintenance notification',
      category: 'system',
      sentAt: '2024-01-15 10:30 AM',
      recipients: 150,
      readCount: 89
    },
    {
      id: '2',
      title: 'New Service Available',
      message: 'Premium services announcement',
      category: 'service',
      sentAt: '2024-01-14 02:15 PM',
      recipients: 200,
      readCount: 156
    }
  ]);

  const [preferences, setPreferences] = useState({
    systemNotifications: true,
    serviceNotifications: true,
    paymentNotifications: true,
    performanceNotifications: true,
    emergencyNotifications: true,
    marketingNotifications: false,
    autoDeleteOld: true,
    maxNotifications: 100
  });

  const categories = [
    { id: 'general', name: 'General', color: '#667eea' },
    { id: 'system', name: 'System', color: '#059669' },
    { id: 'service', name: 'Service', color: '#f59e0b' },
    { id: 'payment', name: 'Payment', color: '#ef4444' },
    { id: 'performance', name: 'Performance', color: '#8b5cf6' },
    { id: 'emergency', name: 'Emergency', color: '#dc2626' }
  ];

  const targetRoles = [
    { id: 'all', name: 'All Users' },
    { id: 'customer', name: 'Customers' },
    { id: 'mechanic', name: 'Mechanics' },
    { id: 'telecaller', name: 'Telecallers' },
    { id: 'serviceexecutive', name: 'Service Executives' },
    { id: 'serviceadvisor', name: 'Service Advisors' },
    { id: 'teamlead', name: 'Team Leads' },
    { id: 'admin', name: 'Admins' }
  ];

  const handleSendNotification = () => {
    if (!notificationTitle.trim() || !notificationMessage.trim()) {
      alert('Please fill in both title and message');
      return;
    }
    alert('Notification sent successfully!');
    setShowSendModal(false);
    setNotificationTitle('');
    setNotificationMessage('');
    setSelectedCategory('general');
    setSelectedTargetRole('all');
  };

  const handleSaveTemplate = () => {
    if (!templateTitle.trim() || !templateMessage.trim()) {
      alert('Title and message are required');
      return;
    }
    if (editingTemplate) {
      setTemplates(templates.map(t => t.id === editingTemplate.id ? {
        ...t, title: templateTitle, message: templateMessage,
        category: templateCategory, targetRole: templateTargetRole
      } : t));
    } else {
      setTemplates([...templates, {
        id: Date.now().toString(),
        title: templateTitle,
        message: templateMessage,
        category: templateCategory,
        targetRole: templateTargetRole
      }]);
    }
    setShowTemplateModal(false);
    setTemplateTitle('');
    setTemplateMessage('');
    setEditingTemplate(null);
  };

  const handleDeleteTemplate = (id) => {
    if (confirm('Delete this template?')) {
      setTemplates(templates.filter(t => t.id !== id));
    }
  };

  const handleDuplicateTemplate = (template) => {
    setTemplates([...templates, {
      ...template,
      id: Date.now().toString(),
      title: `${template.title} (Copy)`
    }]);
  };

  const useTemplate = (template) => {
    setNotificationTitle(template.title);
    setNotificationMessage(template.message);
    setSelectedCategory(template.category);
    setSelectedTargetRole(template.targetRole);
    setShowSendModal(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Notification Manager</h1>
        <p className="text-gray-600 mt-1">Send and manage system notifications</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex space-x-8">
          {[
            { id: 'send', label: 'Send', icon: Send },
            { id: 'history', label: 'History', icon: History },
            { id: 'templates', label: 'Templates', icon: FileText },
            { id: 'preferences', label: 'Preferences', icon: SettingsIcon }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-2 pb-4 border-b-2 transition ${
                activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon size={20} />
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Send Tab */}
      {activeTab === 'send' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="flex space-x-4">
              <button
                onClick={() => setShowSendModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
              >
                <Send size={20} />
                <span>Send Notification</span>
              </button>
              <button
                onClick={() => setActiveTab('templates')}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <FileText size={20} />
                <span>Use Template</span>
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Recent Activity</h2>
            <div className="space-y-3">
              {notificationHistory.slice(0, 3).map(item => (
                <div key={item.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <div className="flex-1">
                    <p className="text-sm text-gray-900">{item.message}</p>
                    <p className="text-xs text-gray-500">{item.sentAt}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          {notificationHistory.map(item => (
            <div key={item.id} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                <span className="px-2 py-1 text-xs font-medium rounded" style={{
                  backgroundColor: categories.find(c => c.id === item.category)?.color + '20',
                  color: categories.find(c => c.id === item.category)?.color
                }}>
                  {item.category}
                </span>
              </div>
              <p className="text-gray-600 mb-4">{item.message}</p>
              <div className="flex items-center space-x-6 text-sm text-gray-500">
                <span>{item.recipients} sent</span>
                <span>{item.readCount} read</span>
                <span>{Math.round((item.readCount / item.recipients) * 100)}% read rate</span>
                <span>{item.sentAt}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Templates Tab */}
      {activeTab === 'templates' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">Notification Templates</h2>
            <button
              onClick={() => {
                setEditingTemplate(null);
                setTemplateTitle('');
                setTemplateMessage('');
                setShowTemplateModal(true);
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
            >
              <Plus size={20} />
              <span>New Template</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {templates.map(template => (
              <div key={template.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-gray-900">{template.title}</h3>
                  <span className="px-2 py-1 text-xs rounded" style={{
                    backgroundColor: categories.find(c => c.id === template.category)?.color + '20',
                    color: categories.find(c => c.id === template.category)?.color
                  }}>
                    {template.category}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-4 line-clamp-3">{template.message}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-500">Target: {template.targetRole}</span>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => useTemplate(template)}
                      className="px-3 py-1 text-xs bg-gray-100 rounded hover:bg-gray-200"
                    >
                      Use
                    </button>
                    <button
                      onClick={() => {
                        setEditingTemplate(template);
                        setTemplateTitle(template.title);
                        setTemplateMessage(template.message);
                        setTemplateCategory(template.category);
                        setTemplateTargetRole(template.targetRole);
                        setShowTemplateModal(true);
                      }}
                      className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDuplicateTemplate(template)}
                      className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                    >
                      <Copy size={14} className="inline" />
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(template.id)}
                      className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                    >
                      <Trash2 size={14} className="inline" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Preferences Tab */}
      {activeTab === 'preferences' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Notification Types</h2>
            <div className="space-y-4">
              {Object.entries(preferences).filter(([key]) => !['autoDeleteOld', 'maxNotifications'].includes(key)).map(([key, value]) => {
                const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
                return (
                  <div key={key} className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{label}</p>
                      <p className="text-sm text-gray-500">Receive notifications for {key.toLowerCase().replace('notifications', '')} events</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) => setPreferences({ ...preferences, [key]: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Send Notification Modal */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Send Notification</h2>
              <button onClick={() => setShowSendModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                <input
                  type="text"
                  value={notificationTitle}
                  onChange={(e) => setNotificationTitle(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter notification title"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                <textarea
                  value={notificationMessage}
                  onChange={(e) => setNotificationMessage(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Enter notification message"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Target Audience</label>
                <select
                  value={selectedTargetRole}
                  onChange={(e) => setSelectedTargetRole(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  {targetRoles.map(role => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => setShowSendModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendNotification}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 flex items-center space-x-2"
                >
                  <Send size={18} />
                  <span>Send Notification</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Template Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingTemplate ? 'Edit Template' : 'Create Template'}
              </h2>
              <button onClick={() => setShowTemplateModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={24} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                <input
                  type="text"
                  value={templateTitle}
                  onChange={(e) => setTemplateTitle(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                <textarea
                  value={templateMessage}
                  onChange={(e) => setTemplateMessage(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={templateCategory}
                  onChange={(e) => setTemplateCategory(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Target Audience</label>
                <select
                  value={templateTargetRole}
                  onChange={(e) => setTemplateTargetRole(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  {targetRoles.map(role => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveTemplate}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 flex items-center space-x-2"
                >
                  <Save size={18} />
                  <span>Save Template</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Notifications;
